/* JsonDocEditor — THE core widget of the v1 builder (BUILD-JOURNEY §1).
 * Raw stored JSON · Copy · Paste · Validate · Validate & Save · dirty guard ·
 * last-saved stamp · BeXO validator errors[] rendered verbatim. */
import React, {
    useEffect,
    useRef
} from 'react';
import {
    AlertTriangle,
    Check,
    ClipboardCopy,
    ClipboardPaste,
    CornerDownRight,
    FilePlus2,
    Loader2,
    Save,
    ShieldCheck,
    Undo2,
    WrapText
} from 'lucide-react';
import {
    notify
} from 'shared/api/notifier';
import CodeMirrorJson, {
    CodeMirrorJsonHandle
} from './CodeMirrorJson';
import CopyForAiButton from './CopyForAiButton';
import { parseValidatorError } from './errorLocation';
import type {
    JsonDocApi
} from './useJsonDoc';

function fmtTime(d: Date | null): string {
    if (!d) return '';
    return d.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    });
}

type Props = {
    doc: JsonDocApi;
    title?: React.ReactNode;
    /** Extra toolbar actions (Try, Re-ingest, Delete…) rendered after Save. */
    actions?: React.ReactNode;
    readOnly?: boolean;
    /** CREATE mode — nothing is stored yet, so the clean state reads
     * 'New document' instead of 'In sync with stored document'. */
    newDoc?: boolean;
};

export default function JsonDocEditor({
    doc,
    title,
    actions,
    readOnly = false,
    newDoc = false
}: Props) {
    const cmRef = useRef<CodeMirrorJsonHandle>(null);

    // Reflect the local parse error as a line highlight inside the editor.
    useEffect(() => {
        if (doc.parseErrorLine != null) {
            cmRef.current?.highlightErrorAt(doc.parseErrorLine);
        } else {
            cmRef.current?.clearErrorHighlight();
        }
    }, [doc.parseErrorLine]);

    /** Click a validator-error/warning row → jump to its location in the
     * editor. Tries the ordered token list (offending value → prefix id →
     * key) and lands on the first that resolves. Best-effort: if none is in
     * the current text (e.g. the doc was edited since validation), say so
     * instead of moving. */
    const jumpTo = (tokens: string[]) => {
        if (tokens.length === 0) return;
        const found = cmRef.current?.revealToken(tokens);
        if (!found) {
            notify(`Could not locate "${tokens[0]}" in the current document`, 'warning');
        }
    };

    /** One styled, clickable issue row — bold/mono location prefix + the
     * message, with a jump affordance when ANY token resolves. Shared by the
     * errors (danger) and warnings (advisory) lists. */
    const renderIssue = (raw: string, i: number) => {
        const { prefix, message, tokens } = parseValidatorError(raw);
        // Clickable only when at least one candidate token is in the document.
        const clickable = tokens.some((t) => doc.text.includes(t));
        return (
            <li
                key={i}
                className={'aps-issue' + (clickable ? ' aps-issue-jump' : '')}
                {...(clickable ? {
                    role: 'button' as const,
                    tabIndex: 0,
                    title: `Jump to "${tokens[0]}" in the document`,
                    onClick: () => jumpTo(tokens),
                    onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            jumpTo(tokens);
                        }
                    }
                } : {})}
            >
                {clickable && (
                    <CornerDownRight size={12} className="aps-issue-icon"
                        aria-hidden="true" />
                )}
                <span className="aps-issue-text">
                    <span className="aps-issue-loc">{prefix}</span>
                    {message ? <span className="aps-issue-msg">: {message}</span> : null}
                </span>
            </li>
        );
    };

    return (
        <div className="aps-editor-screen">
            <div className="aps-editor-toolbar">
                {title ? <div className="aps-editor-title">{title}</div> : null}
                <button className="aps-btn aps-btn-ghost aps-btn-sm" onClick={doc.copyOut}
                    title="Copy the document to the clipboard (take it to your LLM)">
                    <ClipboardCopy size={14} />
                    Copy
                </button>
                <button className="aps-btn aps-btn-ghost aps-btn-sm" onClick={doc.pasteIn}
                    disabled={readOnly}
                    title="Replace the document with the clipboard contents">
                    <ClipboardPaste size={14} />
                    Paste
                </button>
                <button className="aps-btn aps-btn-ghost aps-btn-sm" onClick={doc.format}
                    disabled={readOnly} title="Re-indent the JSON">
                    <WrapText size={14} />
                    Format
                </button>
                <button className="aps-btn aps-btn-secondary aps-btn-sm"
                    onClick={() => doc.runValidate()} disabled={doc.busy || readOnly}
                    title="Run BeXO's validators without saving">
                    <ShieldCheck size={14} />
                    Validate
                </button>
                <button className="aps-btn aps-btn-primary aps-btn-sm"
                    onClick={() => doc.runSave()}
                    disabled={doc.busy || readOnly || !doc.dirty}
                    title="Validate, then save to Redis">
                    {doc.busy
                        ? <Loader2 size={14} className="aps-spin" />
                        : <Save size={14} />}
                    Validate &amp; Save
                </button>
                {actions}
            </div>
            <div className={'aps-editor-frame' + (doc.parseError || doc.errors.length ? ' invalid' : '')}>
                <CodeMirrorJson
                    ref={cmRef}
                    value={doc.text}
                    onChange={readOnly ? undefined : doc.setText}
                    readOnly={readOnly}
                />
            </div>
            <div className="aps-editor-statusline">
                {doc.dirty ? (
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8
                    }}>
                        <span style={{
                            color: 'var(--aps-warning)',
                            fontWeight: 600
                        }}>
                            ● Unsaved changes
                        </span>
                        {!readOnly && (
                            <button className="aps-btn aps-btn-ghost aps-btn-sm"
                                onClick={doc.discard}
                                title={newDoc && !doc.lastSavedAt
                                    ? 'Clear the editor — nothing is stored yet'
                                    : 'Drop the edits and restore the stored document'}>
                                <Undo2 size={12} />
                                Discard changes
                            </button>
                        )}
                    </span>
                ) : newDoc && !doc.lastSavedAt ? (
                    <span style={{
                        color: 'var(--aps-text-muted)'
                    }}>
                        <FilePlus2 size={12} style={{
                            verticalAlign: '-2px'
                        }} /> New document — nothing stored yet
                    </span>
                ) : (
                    <span style={{
                        color: 'var(--aps-success)'
                    }}>
                        <Check size={12} style={{
                            verticalAlign: '-2px'
                        }} /> In sync with stored document
                    </span>
                )}
                {doc.parseError ? (
                    <span style={{
                        color: 'var(--aps-danger)'
                    }}>
                        <AlertTriangle size={12} style={{
                            verticalAlign: '-2px'
                        }} /> {doc.parseError}
                    </span>
                ) : null}
                {doc.pasteNote ? (
                    <span style={{
                        color: 'var(--aps-text-muted)',
                        fontStyle: 'italic'
                    }}>
                        {doc.pasteNote}
                    </span>
                ) : null}
                <span style={{
                    marginLeft: 'auto'
                }}>
                    {doc.lastSavedAt ? `Last saved ${fmtTime(doc.lastSavedAt)}` : ''}
                </span>
            </div>
            {doc.errors.length > 0 && (
                <div className="aps-errors" role="alert">
                    <div className="aps-errors-head">
                        <p className="aps-errors-title">
                            Validator rejected the save — {doc.errors.length} error{doc.errors.length > 1 ? 's' : ''}
                        </p>
                        <CopyForAiButton errors={doc.errors}
                            warnings={doc.warnings} docText={doc.text} />
                    </div>
                    <ul className="aps-issue-list">
                        {doc.errors.map((e, i) => renderIssue(String(e), i))}
                    </ul>
                </div>
            )}
            {doc.warnings.length > 0 && (
                <div className="aps-warnings">
                    <div className="aps-errors-head">
                        <p className="aps-errors-title">
                            Advisories — non-blocking, the save still lands
                        </p>
                        <CopyForAiButton errors={doc.errors}
                            warnings={doc.warnings} docText={doc.text} />
                    </div>
                    <ul className="aps-issue-list">
                        {doc.warnings.map((w, i) => renderIssue(String(w), i))}
                    </ul>
                </div>
            )}
        </div>
    );
}
